import { apiFetch } from "../lib/apiFetch";
import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { db, auth } from "../lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
} from "firebase/firestore";
import {
  MessageCircle,
  Phone,
  Zap,
  CheckCircle2,
  Clock,
  Users,
  TrendingUp,
  ArrowRight,
  Flame,
  CreditCard,
  Download,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { cn } from "../lib/utils";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Lead {
  id: string;
  name?: string;
  phoneNumber: string;
  stage?: string;
  createdAt?: number;
  lastMessage?: string;
}

interface FlowStep {
  day: string;
  title: string;
  stageLabel: string;
  stageKey: string;
  goal: string;
  touchpoints: {
    type: "whatsapp" | "call";
    label: string;
    detail: string;
  }[];
}

// ─── Conversion Flow Definition ───────────────────────────────────────────────

const FLOW_STEPS: FlowStep[] = [
  {
    day: "Day 0",
    title: "First Inquiry",
    stageLabel: "New Lead",
    stageKey: "New Lead",
    goal: "Answer questions instantly, send app download link",
    touchpoints: [
      {
        type: "whatsapp",
        label: "AI Auto-Reply",
        detail: "Instantly answers questions about instructors, timing, streak, pricing. Shares app download link.",
      },
      {
        type: "whatsapp",
        label: "Welcome Message",
        detail: "\"Welcome to YogAIn! 🧘 Here's your download link: [link]. Your 6-day free trial starts the moment you install.\"",
      },
    ],
  },
  {
    day: "Day 1",
    title: "Download Nudge",
    stageLabel: "Qualified leads",
    stageKey: "Qualified leads",
    goal: "Confirm app download, begin onboarding",
    touchpoints: [
      {
        type: "whatsapp",
        label: "Download Reminder",
        detail: "\"Hey [Name]! Have you had a chance to download YogAIn yet? 📱 It takes 2 minutes and your first class is free. [download link]\"",
      },
      {
        type: "call",
        label: "Welcome AI Call",
        detail: "AI calls to welcome, confirm download, walk through first class, answer setup questions.",
      },
    ],
  },
  {
    day: "Day 2–3",
    title: "Onboarding & Streak Start",
    stageLabel: "Qualified leads",
    stageKey: "Qualified leads",
    goal: "Get them into their first class, streak building",
    touchpoints: [
      {
        type: "whatsapp",
        label: "Onboarding Tips",
        detail: "\"Day 2! 🌟 Meet your instructor [Name] — she teaches Hatha every morning at 7AM. Open the app → Live Classes to join!\"",
      },
      {
        type: "whatsapp",
        label: "Streak Intro",
        detail: "\"Your streak has started! 🔥 Practice every day to keep it going. Streaks unlock badges and exclusive content.\"",
      },
    ],
  },
  {
    day: "Day 4–5",
    title: "Engagement Check-in",
    stageLabel: "Hot leads",
    stageKey: "Hot leads",
    goal: "Reinforce value, handle objections, prime for conversion",
    touchpoints: [
      {
        type: "call",
        label: "Mid-Trial AI Call",
        detail: "AI checks in on their experience, answers objections (price, time, features), introduces pricing naturally.",
      },
      {
        type: "whatsapp",
        label: "Value Message",
        detail: "\"You're almost halfway through your trial! 💪 How are you feeling? Your [X]-day streak is impressive — keep it up!\"",
      },
    ],
  },
  {
    day: "Day 5–6",
    title: "Conversion Close",
    stageLabel: "Hot leads",
    stageKey: "Hot leads",
    goal: "Get the payment. Don't let trial expire without asking.",
    touchpoints: [
      {
        type: "call",
        label: "Conversion AI Call",
        detail: "AI presents plan options, handles price objections, creates urgency (trial ends soon), asks for payment directly.",
      },
      {
        type: "whatsapp",
        label: "Payment Link",
        detail: "\"[Name], your trial ends soon! Continue your [X]-day streak with our ₹499/month plan 🧘 Pay here: [payment link]\"",
      },
      {
        type: "whatsapp",
        label: "Final Close",
        detail: "\"Last chance! ⏰ Don't lose your streak. Continue YogAIn today — [payment link]. Special 20% off if you pay before midnight!\"",
      },
    ],
  },
];

// ─── Sequence Templates ───────────────────────────────────────────────────────

const YOGAIN_SEQUENCES = [
  {
    name: "YogAIn — Day 0: Welcome & Download",
    description: "Immediate welcome message with app download link sent to every new lead.",
    triggerOnNewLead: true,
    triggerOnStage: undefined,
    messages: [
      {
        timing: "immediate",
        content:
          "Namaste {{name}}! 🙏 Welcome to YogAIn Wellness.\n\nYou've taken the first step toward a healthier, more mindful life.\n\n📱 Download the app here and start your FREE 6-day trial:\n{{appDownloadLink}}\n\nFeel free to ask me anything — I'm here to help! 😊",
      },
    ],
  },
  {
    name: "YogAIn — Day 1: Download Nudge",
    description: "Sent 24h after signup if they haven't confirmed downloading.",
    triggerOnNewLead: false,
    messages: [
      {
        timing: "delay",
        delayMinutes: 1440,
        content:
          "Hey {{name}}! 👋\n\nHave you downloaded YogAIn yet? It only takes 2 minutes!\n\n📲 {{appDownloadLink}}\n\nYour 6-day FREE trial is waiting. First class is on us. 🧘‍♀️",
      },
    ],
  },
  {
    name: "YogAIn — Day 2: Meet Your Instructor",
    description: "Sent on day 2 to introduce instructors and live class schedule.",
    triggerOnNewLead: false,
    messages: [
      {
        timing: "delay",
        delayMinutes: 2880,
        content:
          "Day 2! 🌟 {{name}}, have you joined your first class yet?\n\nOur instructors are live every morning at 7AM and evening at 7PM IST. Open the app → Live Classes to join!\n\n💡 Tip: Try a Hatha class first if you're just starting out. It's the perfect foundation.",
      },
    ],
  },
  {
    name: "YogAIn — Day 3: Streak Motivation",
    description: "Day 3 motivation message about the streak system.",
    triggerOnNewLead: false,
    messages: [
      {
        timing: "delay",
        delayMinutes: 4320,
        content:
          "🔥 {{name}}, your streak is building!\n\nDid you know: A 21-day streak unlocks exclusive advanced classes and a special badge?\n\nKeep it up! Open the app and practice today — even 15 minutes counts. You've got this 💪",
      },
    ],
  },
  {
    name: "YogAIn — Day 5: Payment Push",
    description: "Conversion message with payment link before trial ends.",
    triggerOnNewLead: false,
    messages: [
      {
        timing: "delay",
        delayMinutes: 7200,
        content:
          "{{name}}, your YogAIn trial is almost over 😢\n\nDon't lose your streak and progress! Continue your practice with our special offer:\n\n✅ Monthly: ₹499/month\n✅ Annual: ₹2,999/year (Save 50%! 🎉)\n\n💳 Pay here and continue seamlessly:\n{{paymentLink}}\n\nYou've come this far — keep going! 🧘",
      },
    ],
  },
  {
    name: "YogAIn — Day 6: Final Close",
    description: "Last message — trial ends today, final urgency push.",
    triggerOnNewLead: false,
    messages: [
      {
        timing: "delay",
        delayMinutes: 8640,
        content:
          "⏰ {{name}}, this is it — your trial ends TODAY!\n\nDon't let your streak and progress disappear. 💔\n\nContinue with YogAIn — just ₹499/month or ₹2,999/year.\n\n🔗 {{paymentLink}}\n\nIf you have any questions or need help deciding, just reply and I'll help! 🙏",
      },
    ],
  },
];

const YOGAIN_CALL_AGENDAS = [
  {
    name: "YogAIn — Day 1 Welcome Call",
    description: "Welcome call after signup. Confirm app download, walk through first class, answer setup questions.",
    useKnowledgeBase: true,
    greetingPrompt:
      "Greet the lead warmly by name in Hindi or English based on their name. Say you're calling from YogAIn to welcome them and make sure their app setup went smoothly. Keep it short and friendly.",
    systemPrompt: `You are a warm, helpful AI from YogAIn Wellness calling to welcome a new lead.

Your goal for this call:
1. Confirm they downloaded the YogAIn app. If not, give them the download link from the knowledge base.
2. Walk them through the app: how to find instructors, how to join a live class.
3. Explain the streak system — it starts the day they complete their first session.
4. Answer any questions they have about the app.
5. End by encouraging them to join a class today and set a reminder.

Rules:
- Be warm, friendly, not salesy. This is an onboarding call, not a sales call.
- Match the caller's language (Hindi/Hinglish or English).
- Keep each response 2-3 sentences max.
- No markdown, no bullet points while speaking.
- Say __END_CALL__ when done.`,
  },
  {
    name: "YogAIn — Mid-Trial Check-in (Day 4-5)",
    description: "Check in on their trial experience, handle objections, naturally introduce pricing.",
    useKnowledgeBase: true,
    greetingPrompt:
      "Greet them warmly by name. Say you're from YogAIn and wanted to check in on how their trial is going. Sound genuinely curious, not like a sales call.",
    systemPrompt: `You are a friendly AI from YogAIn Wellness doing a mid-trial check-in call.

Your goal:
1. Ask how their experience has been. Listen to what they say.
2. Celebrate any wins (classes completed, streaks, instructor they like).
3. If they mention any friction (app issues, no time, didn't download) → solve it immediately.
4. Naturally introduce the transition from trial to paid: "Your trial ends in a couple of days — I wanted to make sure you're getting the most out of it."
5. Briefly mention pricing if the conversation is going well. Don't push. Plant the seed.
6. End with a clear next step: "Try one more class before your trial ends — the morning Hatha class is great."

Common objections and how to handle them:
- "I haven't had time" → "Even 15 minutes a day counts. The evening class is only 30 minutes."
- "I'm not sure about paying" → "Totally fair. Can I ask what would make it worth it for you?"
- "I need to think about it" → "Of course! I'll send you the pricing details on WhatsApp. No pressure."

Rules:
- Be genuinely warm. Care about their wellness journey, not just the sale.
- Match language (Hindi/Hinglish or English).
- 2-3 sentences per response.
- Say __END_CALL__ when done.`,
  },
  {
    name: "YogAIn — Conversion Close (Day 5-6)",
    description: "Direct conversion call. Trial ends soon. Present plans, handle objections, ask for payment.",
    useKnowledgeBase: true,
    greetingPrompt:
      "Greet them warmly by name. Tell them you're calling because their YogAIn trial is ending very soon and you want to make sure they don't lose their progress and streak.",
    systemPrompt: `You are an AI sales agent from YogAIn Wellness on a conversion call. The lead's trial ends very soon.

Your goal: Get them to choose a plan and pay.

Your approach:
1. Open with urgency + empathy: "Your trial ends [today/tomorrow] and I'd hate for you to lose your streak and progress."
2. Summarize their journey: "You've completed [X] classes and built a great habit."
3. Present plans clearly and simply:
   - Monthly: ₹499/month — great for trying out
   - Annual: ₹2,999/year — saves 50%, best value, most popular
4. Ask directly: "Which plan feels right for you?"
5. Handle objections:
   - "Too expensive" → "₹499 is less than one yoga class in a studio. You get unlimited classes every month."
   - "Need time to think" → "I totally understand. Can I send you the payment link on WhatsApp right now? You can pay anytime today — trial is still active."
   - "Not sure I'll use it" → "Your streak shows you're already using it! The habit is forming."
   - "Partner needs to approve" → "Of course. I'll send the details on WhatsApp. Our annual plan at ₹2,999 is a great proposal."
6. If they agree: "Amazing! I'm so glad. I'll send the payment link on WhatsApp right now. You'll continue seamlessly."
7. If they decline: Try once more with a softer angle, then respect it. "No worries. We'll keep your progress saved for 30 days in case you change your mind."

Rules:
- Be warm but direct. It's okay to ask for the sale.
- Match language (Hindi/Hinglish or English).
- 2-3 sentences per response.
- Say __END_CALL__ when call is fully complete.`,
  },
];

// ─── Stage Colors ─────────────────────────────────────────────────────────────

const STAGE_STYLES: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  "New Lead":       { bg: "bg-orange-50",  text: "text-orange-700",  border: "border-orange-200", dot: "bg-orange-400" },
  "Qualified leads":{ bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200",   dot: "bg-blue-400" },
  "Hot leads":      { bg: "bg-green-50",   text: "text-green-700",   border: "border-green-200",  dot: "bg-green-400" },
  "Lead Won":       { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200",dot: "bg-emerald-500" },
  "Cold Leads":     { bg: "bg-slate-50",   text: "text-slate-600",   border: "border-slate-200",  dot: "bg-slate-400" },
  "Leads lost":     { bg: "bg-purple-50",  text: "text-purple-700",  border: "border-purple-200", dot: "bg-purple-400" },
};

// ─── Main Component ───────────────────────────────────────────────────────────

export function ConversionFlow() {
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const queryClient = useQueryClient();
  const ownerId = auth.currentUser?.uid || "";

  const { data: leads = [] } = useQuery<Lead[]>({
    queryKey: ["whatsapp", "chats", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const res = await apiFetch(`/api/whatsapp/chats?ownerId=${ownerId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!ownerId,
    refetchInterval: 10000,
  });

  const { data: sequences = [] } = useQuery({
    queryKey: ["sequences", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const q = query(collection(db, "sequences"), where("ownerId", "==", ownerId));
      const snap = await getDocs(q);
      return snap.docs;
    },
    enabled: !!ownerId,
  });

  const { data: agendas = [] } = useQuery({
    queryKey: ["callAgendas", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const q = query(collection(db, "callAgendas"), where("ownerId", "==", ownerId));
      const snap = await getDocs(q);
      return snap.docs;
    },
    enabled: !!ownerId,
  });

  const seedTemplates = useCallback(async () => {
    if (!ownerId || seeding) return;
    setSeeding(true);
    try {
      const now = Date.now();

      // Seed WhatsApp sequences
      for (const seq of YOGAIN_SEQUENCES) {
        const id = crypto.randomUUID();
        await setDoc(doc(db, "sequences", id), {
          ...seq,
          id,
          ownerId,
          createdAt: now,
          updatedAt: now,
        });
      }

      // Seed call agendas
      for (const agenda of YOGAIN_CALL_AGENDAS) {
        const id = crypto.randomUUID();
        await setDoc(doc(db, "callAgendas", id), {
          ...agenda,
          id,
          ownerId,
          createdAt: now,
          updatedAt: now,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["sequences"] });
      queryClient.invalidateQueries({ queryKey: ["callAgendas"] });
      setSeeded(true);
    } finally {
      setSeeding(false);
    }
  }, [ownerId, seeding, queryClient]);

  const stageCounts = leads.reduce<Record<string, number>>((acc, l) => {
    const s = l.stage || "New Lead";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const totalLeads = leads.length;
  const wonLeads = stageCounts["Lead Won"] || 0;
  const hotLeads = stageCounts["Hot leads"] || 0;
  const convRate = totalLeads > 0 ? Math.round((wonLeads / totalLeads) * 100) : 0;

  const hasTemplates = sequences.length > 0 || agendas.length > 0;

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-5xl mx-auto p-6 space-y-8">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-green-600" />
              YogAIn Conversion Flow
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              6-day hybrid strategy: WhatsApp messages + AI calls → trial to paid.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {seeded && (
              <span className="text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                Templates seeded!
              </span>
            )}
            <button
              onClick={seedTemplates}
              disabled={seeding}
              className="px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 shadow-sm"
            >
              {seeding ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : hasTemplates ? (
                <RefreshCw className="w-4 h-4" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              {seeding ? "Seeding..." : hasTemplates ? "Re-seed Templates" : "Seed All YogAIn Templates"}
            </button>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "Total Leads", value: totalLeads, icon: Users, color: "text-blue-600" },
            { label: "Hot Leads", value: hotLeads, icon: Flame, color: "text-orange-600" },
            { label: "Converted", value: wonLeads, icon: CreditCard, color: "text-emerald-600" },
            { label: "Conv. Rate", value: `${convRate}%`, icon: TrendingUp, color: "text-green-600" },
          ].map((m) => (
            <div key={m.label} className="bg-white border rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <m.icon className={`w-4 h-4 ${m.color}`} />
                <span className="text-xs text-slate-500 font-medium">{m.label}</span>
              </div>
              <p className={`text-2xl font-bold ${m.color}`}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Template Status */}
        <div className="bg-white border rounded-xl shadow-sm p-5">
          <h2 className="font-semibold text-slate-800 mb-3">Template Status</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className={cn("border rounded-lg p-4", sequences.length > 0 ? "border-green-200 bg-green-50" : "border-slate-200 bg-slate-50")}>
              <div className="flex items-center gap-2 mb-1">
                <MessageCircle className={`w-4 h-4 ${sequences.length > 0 ? "text-green-600" : "text-slate-400"}`} />
                <span className="text-sm font-semibold text-slate-800">WhatsApp Sequences</span>
                {sequences.length > 0 && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto" />}
              </div>
              <p className="text-2xl font-bold text-slate-900">{sequences.length}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {sequences.length === 0
                  ? "No sequences. Click 'Seed Templates' to create the 6-day flow."
                  : "Day 0–6 messages configured"}
              </p>
            </div>
            <div className={cn("border rounded-lg p-4", agendas.length > 0 ? "border-green-200 bg-green-50" : "border-slate-200 bg-slate-50")}>
              <div className="flex items-center gap-2 mb-1">
                <Phone className={`w-4 h-4 ${agendas.length > 0 ? "text-green-600" : "text-slate-400"}`} />
                <span className="text-sm font-semibold text-slate-800">AI Call Agendas</span>
                {agendas.length > 0 && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto" />}
              </div>
              <p className="text-2xl font-bold text-slate-900">{agendas.length}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {agendas.length === 0
                  ? "No call agendas. Click 'Seed Templates' to create them."
                  : "Welcome, check-in, and conversion calls configured"}
              </p>
            </div>
          </div>
        </div>

        {/* Flow Timeline */}
        <div className="space-y-3">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            6-Day Conversion Timeline
          </h2>

          <div className="space-y-3">
            {FLOW_STEPS.map((step, idx) => (
              <div key={idx} className="bg-white border rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-center gap-4 p-4 border-b bg-slate-50">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex flex-col items-center justify-center text-white shrink-0 shadow-sm">
                    <span className="text-[9px] font-semibold opacity-80">{step.day.split(" ")[0].toUpperCase()}</span>
                    <span className="text-lg font-black leading-none">{step.day.split(" ")[1]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-slate-900">{step.title}</h3>
                      <span className={cn("text-[10px] px-2 py-0.5 rounded-full border font-medium",
                        STAGE_STYLES[step.stageKey]?.bg,
                        STAGE_STYLES[step.stageKey]?.text,
                        STAGE_STYLES[step.stageKey]?.border
                      )}>
                        {step.stageLabel}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{step.goal}</p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-400 shrink-0">
                    <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                      {step.touchpoints.filter(t => t.type === "whatsapp").length} msg
                    </span>
                    {step.touchpoints.some(t => t.type === "call") && (
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                        1 call
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-4 grid gap-3 sm:grid-cols-2">
                  {step.touchpoints.map((tp, tIdx) => (
                    <div
                      key={tIdx}
                      className={cn(
                        "rounded-lg border p-3",
                        tp.type === "whatsapp"
                          ? "bg-green-50 border-green-200"
                          : "bg-blue-50 border-blue-200"
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        {tp.type === "whatsapp" ? (
                          <MessageCircle className="w-4 h-4 text-green-600 shrink-0" />
                        ) : (
                          <Phone className="w-4 h-4 text-blue-600 shrink-0" />
                        )}
                        <span className={cn("text-xs font-bold", tp.type === "whatsapp" ? "text-green-800" : "text-blue-800")}>
                          {tp.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">{tp.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lead Pipeline by Stage */}
        <div className="space-y-3">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            Current Lead Pipeline
          </h2>

          {leads.length === 0 ? (
            <div className="bg-white border rounded-xl p-10 text-center text-slate-400 text-sm">
              No leads yet. Leads will appear here as they contact you on WhatsApp.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(STAGE_STYLES).map(([stage, style]) => {
                const stageLeads = leads.filter((l) => (l.stage || "New Lead") === stage);
                if (stageLeads.length === 0) return null;
                return (
                  <div key={stage} className={cn("border rounded-xl p-4 shadow-sm", style.bg, style.border)}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={cn("w-2 h-2 rounded-full", style.dot)} />
                        <span className={cn("text-xs font-bold", style.text)}>{stage}</span>
                      </div>
                      <span className={cn("text-lg font-black", style.text)}>{stageLeads.length}</span>
                    </div>
                    <div className="space-y-2">
                      {stageLeads.slice(0, 4).map((lead) => (
                        <div key={lead.id} className="bg-white/70 rounded-lg px-3 py-2">
                          <p className="text-xs font-semibold text-slate-800 truncate">{lead.name || "Unnamed"}</p>
                          <p className="text-[10px] text-slate-500 truncate">{lead.phoneNumber}</p>
                        </div>
                      ))}
                      {stageLeads.length > 4 && (
                        <p className={cn("text-[10px] font-medium text-center", style.text)}>
                          +{stageLeads.length - 4} more
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">How the hybrid strategy works</h3>
          <div className="grid sm:grid-cols-2 gap-3 text-xs text-slate-600">
            <div className="flex items-start gap-2">
              <MessageCircle className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800">WhatsApp (Auto Follow-up)</p>
                <p>Sequences run automatically based on timing. Set them up in "Auto Follow-up".</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800">AI Calls (Exotel + Gemini)</p>
                <p>Triggered manually or via automation from the "AI Calling" page. Real voice calls.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800">AI Auto-Reply (WhatsApp)</p>
                <p>Answers questions instantly when a lead messages. Enable per-lead in Dashboard.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800">Stage Progression</p>
                <p>AI auto-updates lead stage based on conversation (New → Qualified → Hot → Won).</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
